import { useSelector } from 'react-redux';
import { CompanyDetailsInterface } from '../../services/exports/Interfaces';
import { selectCompany } from '../../store/slices/InitialDataSlice';

export enum SessionStorageReservedKey {
  OrderMethodInfoStatus = 'order_method_info_status',
  OrderMethodModalDismissed = 'order_method_modal_dismissed',
  AllergensCardTipSeen = 'allergens_card_tip_seen',
  AllergensModalTipSeen = 'allergens_modal_tip_seen',
  AllergensModalConfirmed = 'allergens_modal_confirmed',
  PaymentElementRetryOrder = 'payment_element_retry_order',
}

export const composeSessionStorageKey = (companyId: number | undefined, key: SessionStorageReservedKey) =>
  `company-${companyId}-${key}`;

/** Removes `key` for every company in this tab, e.g. on a logout, when the company is already gone. */
export const removeSessionStorageKeyForAllCompanies = (key: SessionStorageReservedKey) => {
  const pattern = new RegExp(`^company-\\d+-${key}$`);

  try {
    const matching = Array.from({ length: sessionStorage.length }, (_, index) => sessionStorage.key(index)).filter(
      (storedKey) => storedKey && pattern.test(storedKey),
    );

    matching.forEach((storedKey) => sessionStorage.removeItem(storedKey));
  } catch {
    // Storage refused (private mode): there is nothing in it to remove.
  }
};

export default function useSessionStorage(customCompany?: CompanyDetailsInterface) {
  const defaultCompany = useSelector(selectCompany);
  const company = customCompany ?? defaultCompany;

  const composeKey = (key: SessionStorageReservedKey) => composeSessionStorageKey(company?.id, key);

  const getItem = (key: SessionStorageReservedKey, defaultValue?: any): null | string =>
    sessionStorage.getItem(composeKey(key)) ?? defaultValue;
  const setItem = (key: SessionStorageReservedKey, value: any) => sessionStorage.setItem(composeKey(key), value);
  const removeItem = (key: SessionStorageReservedKey) => sessionStorage.removeItem(composeKey(key));

  return {
    getItem,
    setItem,
    removeItem,
  };
}
